import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { emailTrackingService } from '../../services/emailTrackingService';
import Toast from '../../components/ui/Toast';

import DeliveryMetricsOverview from './components/DeliveryMetricsOverview';
import EmailLogsGrid from './components/EmailLogsGrid';
import DeliveryTrendsChart from './components/DeliveryTrendsChart';
import FailureAnalysis from './components/FailureAnalysis';

const EmailDeliveryMonitoringDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [emailLogs, setEmailLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    recipientType: 'all',
    searchEmail: '',
    dateRange: '7' // days
  });
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadDashboardData();
    setupRealtimeSubscription();

    return () => {
      if (realtimeChannel) {
        emailTrackingService?.unsubscribe(realtimeChannel);
      }
    };
  }, [user, userProfile, navigate]);

  useEffect(() => {
    loadEmailLogs();
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const endDate = new Date();
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - parseInt(filters?.dateRange || 7));

      const [metricsData, statsData] = await Promise.all([
        emailTrackingService?.getDeliveryMetrics(
          startDate?.toISOString(),
          endDate?.toISOString()
        ),
        emailTrackingService?.getDailyStats(parseInt(filters?.dateRange || 7))
      ]);

      setMetrics(metricsData);
      setDailyStats(statsData);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailLogs = async () => {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - parseInt(filters?.dateRange || 7));

      const logs = await emailTrackingService?.getEmailLogs({
        ...filters,
        startDate: startDate?.toISOString()
      });

      setEmailLogs(logs);
    } catch (err) {
      console.error('Error loading email logs:', err);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = emailTrackingService?.subscribeToEmailLogs(
      (newLog) => {
        console.log('New email log:', newLog);
        setEmailLogs(prev => [newLog, ...prev]);
        loadDashboardData(); // Refresh metrics
        setToast({
          message: `New email sent to ${newLog?.recipient_email}`,
          type: 'info'
        });
      },
      (updatedLog) => {
        console.log('Email log updated:', updatedLog);
        setEmailLogs(prev => 
          prev?.map(log => log?.id === updatedLog?.id ? updatedLog : log)
        );
        loadDashboardData(); // Refresh metrics
        
        // Show toast based on status
        if (updatedLog?.status === 'delivered') {
          setToast({
            message: `Email delivered to ${updatedLog?.recipient_email}`,
            type: 'success'
          });
        } else if (updatedLog?.status === 'bounced' || updatedLog?.status === 'failed') {
          setToast({
            message: `Email ${updatedLog?.status}: ${updatedLog?.recipient_email}`,
            type: 'error'
          });
        } else if (updatedLog?.status === 'opened') {
          setToast({
            message: `Email opened by ${updatedLog?.recipient_email}`,
            type: 'success'
          });
        }
      }
    );
    setRealtimeChannel(channel);
  };

  const handleRefresh = () => {
    loadDashboardData();
    loadEmailLogs();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003554] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-[#1FB5A5] mx-auto" />
            <p className="mt-4 text-gray-300">Loading email delivery dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003554] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto" />
            <p className="mt-4 text-red-400">{error}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003554] to-[#002B45]">
      <Helmet>
        <title>Email Delivery Monitoring Dashboard - Resend Integration Command Center | Northstar Analytics</title>
        <meta name="description" content="Real-time monitoring dashboard for all booking confirmation emails sent via Resend with delivery status, failures, and retry history" />
      </Helmet>
      <Header />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-elevation-2 p-8 mb-8 border border-white/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-[#1FB5A5]/20 rounded-lg flex items-center justify-center">
                    <Icon name="Mail" size={24} className="text-[#1FB5A5]" />
                  </div>
                  <h1 className="text-3xl font-bold text-white">
                    Email Delivery Monitoring Dashboard
                  </h1>
                </div>
                <p className="text-gray-300 ml-15">
                  Resend Integration Command Center - Real-Time Email Tracking
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex gap-3">
                <select
                  value={filters?.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e?.target?.value)}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1FB5A5] backdrop-blur-sm"
                >
                  <option value="1" className="bg-[#002B45] text-white">Last 24 Hours</option>
                  <option value="7" className="bg-[#002B45] text-white">Last 7 Days</option>
                  <option value="30" className="bg-[#002B45] text-white">Last 30 Days</option>
                  <option value="90" className="bg-[#002B45] text-white">Last 90 Days</option>
                </select>
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleRefresh}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Live Status Indicator */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-elevation-1 p-4 mb-8 border border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                </div>
                <span className="text-white font-medium">Live Monitoring Active</span>
                <span className="text-gray-300 text-sm">Real-time webhook updates enabled</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Icon name="Database" size={16} />
                <span>{emailLogs?.length} emails tracked</span>
              </div>
            </div>
          </div>

          {/* Delivery Metrics Overview */}
          <DeliveryMetricsOverview metrics={metrics} />

          {/* Delivery Trends Chart */}
          <DeliveryTrendsChart data={dailyStats} />

          {/* Failure Analysis */}
          <FailureAnalysis emailLogs={emailLogs} />

          {/* Email Logs Grid */}
          <EmailLogsGrid 
            emailLogs={emailLogs}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailDeliveryMonitoringDashboard;